import { useState, useRef } from "react";
import styled from "styled-components";
import { Tooltip } from "react-tooltip";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { COUNTRIES, COUNTRY_ICONS } from "constants/countries";
import { dateFormatted, getTimeAMPM } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import SectionTitle from "components/text/SectionTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ContextMenu from "components/menu/ContextMenu";

import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-white-icon.svg";
import checkIcon from "assets/img/icons/icons8-check.svg";
import checkWhiteIcon from "assets/img/icons/icons8-check-white.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import circleGreyIcon from "assets/img/icons/icons8-circle-grey.svg";
import filterIcon from "assets/img/icons/icons8-filter.svg";
import filterWhiteIcon from "assets/img/icons/icons8-filter-white.svg";

const SubscriberListSection = styled.div``;

const Header = styled.div`
  margin-bottom: 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const LeadList = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_NO_SIDES};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 520px;

  border-spacing: 0;
`;

const TableHead = styled.thead``;

const Label = styled.th`
  padding: 0 5px 12px 5px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  font-size: 17px;
  font-weight: 400;
  text-align: left;
`;

const TableBody = styled.tbody``;

const ListLine = styled.tr`
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: pointer;
  }
`;

const Cell = styled.td`
  padding: 8px 4px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  white-space: nowrap;
  font-size: 15px;
`;

const CellVeryLeft = styled.div`
  padding-left: 30px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-left: 20px;
  }
`;

const CellVeryRight = styled.div`
  padding-right: 30px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-right: 20px;
  }
`;

const CellLineValueWrap = styled.div`
  padding-right: 10px;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const CopyIconBox = styled.div``;

const BadgeWrap = styled.div`
  display: flex;
`;

const Badge = styled.div`
  height: 18px;
  padding: 0 10px 2px;
  min-width: 38px;
  box-sizing: border-box;

  font-size: 12px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) =>
    props.verified ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 6px;
    margin-bottom: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const NoResults = styled.div`
  width: 100%;
  margin: 25px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div``;

const CountryIcon = styled.div`
  padding-top: 1px;

  width: 18px;
  min-width: 18px;
  height: 18px;

  &:hover {
    cursor: help;
  }
`;

const EllipsisValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default ({ data, limit, page, paginate, openLead }) => {
  const numOfResults = data.responderSubscribersData.numOfResults;

  const list = data.responderSubscribersData.subscribers.map((subscriber) => {
    const {
      id,
      created_at,
      name,
      email,
      location_country,
      sent_emails_aggregate,
    } = subscriber;

    let countryIcon = null;
    let countryName = "";

    if (location_country) {
      const country = COUNTRIES[location_country];

      if (country) {
        countryName = country.name;

        countryIcon = COUNTRY_ICONS[countryName]
          ? COUNTRY_ICONS[countryName].icon
          : null;
      }
    }

    if (!countryIcon) countryIcon = circleGreyIcon;

    return (
      <ListLine key={id} onClick={() => openLead(id)}>
        <Cell>
          <CellVeryLeft>
            {dateFormatted(new Date(created_at), false, false) +
              ", " +
              getTimeAMPM(new Date(created_at))}
          </CellVeryLeft>
        </Cell>
        <Cell width="170px">
          <CellLineValueWrap>
            <CountryIcon
              data-tooltip-id="countryTip"
              data-tooltip-html={
                location_country ? countryName : "Unknown location"
              }
              data-tooltip-place="top"
              data-tooltip-delay-show={500}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={countryIcon} width="100%" alt={countryName} />
            </CountryIcon>
            <Tooltip id="countryTip" className="tooltip" />
            <EllipsisValue>{name}</EllipsisValue>
          </CellLineValueWrap>
        </Cell>
        <Cell width="170px">{email}</Cell>
        <Cell>
          <CellVeryRight>
            <CellLineValueWrap>
              <EllipsisValue>
                {sent_emails_aggregate.aggregate.count}
              </EllipsisValue>
            </CellLineValueWrap>
          </CellVeryRight>
        </Cell>
      </ListLine>
    );
  });

  // prepare pagination
  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  return (
    <SubscriberListSection>
      <Header>
        <Title>Subscribers</Title>
      </Header>
      <LeadList>
        <TableWrap className="table-wrap">
          {list.length > 0 && (
            <ListTable>
              <TableHead>
                <tr>
                  <Label>
                    <CellVeryLeft>Added</CellVeryLeft>
                  </Label>
                  <Label>Name</Label>
                  <Label>Email</Label>
                  <Label>
                    <CellVeryRight>Sent Emails</CellVeryRight>
                  </Label>
                </tr>
              </TableHead>
              <TableBody>{list}</TableBody>
            </ListTable>
          )}
          {list.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <img src={blockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              <NoResultsText>You don't have any subscribers yet.</NoResultsText>
            </NoResults>
          )}
        </TableWrap>
        {showPagination && (
          <Pagination>
            {page > 1 && (
              <ButtonRoundIcon
                icon={leftIcon}
                hoverIcon={leftWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                iconSize="16px"
                buttonSize="32px"
                onClick={() => paginate("previous")}
              />
            )}
            {page < maxPage && (
              <ButtonRoundIcon
                icon={rightIcon}
                hoverIcon={rightWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                iconSize="16px"
                buttonSize="32px"
                onClick={() => paginate("next")}
              />
            )}
          </Pagination>
        )}
      </LeadList>
    </SubscriberListSection>
  );
};
